import Icon from '../../../Icon/Icon';
import { useTranslation } from 'react-i18next';
import { useService } from '../../../../containers/UseService';
import { useAppSnackbars } from '../../../../hooks/utils/useAppSnackbars';
import CommonButton from '../../../Buttons/CommonButton';
import ForecastDistributionChart from '../../WizardAudiences/ForecastMetrics/ForecastDistributionChart';

const CampaignReady = ({
    basics,
    location,
    screensState,
    isLaunchBlockedByActiveCampaign = false,
    forecastStatus = 'idle',
    forecastPoints = [],
    forecastCurrencySymbol = '',
    onRetryForecast,
}) => {
    const { t } = useTranslation();
    const { openError } = useAppSnackbars();

    const service = useService();

    const handleLaunch = async () => {
        if (isLaunchBlockedByActiveCampaign || forecastStatus !== 'success') {
            return;
        }

        try {
            await service.launchCampaign(basics, location, screensState);
        } catch (unusedError) {
            openError(
                t(
                    'sections.campaigns.new-campaign.wizard-launch.launch.campaign-not-launched-message'
                )
            );
        }
    };

    const isLaunchBlocked =
        isLaunchBlockedByActiveCampaign || forecastStatus !== 'success';

    return (
        <div className={`block py-4 text-center`}>
            <div className="inline-block w-fit max-w-full mx-auto">
                <Icon
                    name="grin-wink"
                    fixedWidth
                    size="2x"
                    className="mr-1 text-yellow-300"
                />{' '}
                {
                    <span className="font-light text-2xl">
                        {t(
                            'sections.campaigns.new-campaign.wizard-review.review.info'
                        )}
                        !
                    </span>
                }
                <div className="mt-2 lex flex-col">
                    <p className="text-lg font-light">
                        {t(
                            'sections.campaigns.new-campaign.wizard-review.review.campaign-ready-to-start'
                        )}{' '}
                    </p>

                    <p className="text-gray-700 leading-6 font-light mt-4">
                        {t(
                            'sections.campaigns.new-campaign.wizard-review.review.there-are-screens-that-need-to-approve'
                        )}
                    </p>
                    <p className="text-gray-500 leading-6 font-light -mt-1">
                        {t(
                            'sections.campaigns.new-campaign.wizard-review.review.we-will-let-you-know-as-soon'
                        )}
                    </p>
                </div>
                <div className="flex justify-center m-3">
                    {' '}
                    <CommonButton
                        onClick={handleLaunch}
                        styleType={isLaunchBlocked ? 'blocked' : 'primary'}
                        className="text-lg px-6 py-3 flex items-center justify-center mt-4"
                        testId="launch-campaign-button"
                        disabled={isLaunchBlocked}
                        useLoader={true}>
                        {t(
                            'sections.campaigns.new-campaign.wizard-review.review.launch-the-campaign-now'
                        )}
                        <Icon
                            name="rocket-launch"
                            className="text-base ml-2"
                        />
                    </CommonButton>
                </div>
                {isLaunchBlockedByActiveCampaign && (
                    <p className="text-sm text-red-600 font-light -mt-1">
                        {t(
                            'sections.campaigns.new-campaign.wizard-review.review.save-active-before-launch'
                        )}
                    </p>
                )}
            </div>
            {forecastStatus === 'loading' && (
                <ForecastDistributionChart
                    points={[]}
                    isLoading
                    currencySymbol={forecastCurrencySymbol}
                />
            )}
            {(forecastStatus === 'success' || forecastStatus === 'empty') && (
                <>
                    <ForecastDistributionChart
                        points={forecastPoints}
                        currencySymbol={forecastCurrencySymbol}
                    />
                    {forecastStatus === 'empty' && (
                        <CommonButton
                            onClick={onRetryForecast}
                            styleType="secondary"
                            className="mt-3"
                            label={t(
                                'sections.campaigns.new-campaign.wizard-review.review.retry-forecast'
                            )}
                        />
                    )}
                </>
            )}
            {forecastStatus === 'error' && (
                <div
                    className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-center"
                    role="alert">
                    <p className="text-sm text-red-700">
                        {t(
                            'sections.campaigns.new-campaign.wizard-review.review.forecast-error'
                        )}
                    </p>
                    <CommonButton
                        onClick={onRetryForecast}
                        styleType="secondary"
                        className="mt-3"
                        label={t(
                            'sections.campaigns.new-campaign.wizard-review.review.retry-forecast'
                        )}
                    />
                </div>
            )}
        </div>
    );
};

export default CampaignReady;
